/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Image } from 'antd'
import { useHistory } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { pendingInvitationsCopy } from '@sportninja/common/features/invitation/constants/pendingInvitationsCopy'
import { toPendingInvitationRow } from '@sportninja/common/features/invitation/helper/pendingInvitations'
import type { MyInvitation } from '@sportninja/common/services/invitation/types'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { useNotificationsPanel } from 'src/components/NotificationsPanelContext'
import BaseModal from 'src/components/ts-components/BaseModal'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import { MODAL_TOKENS } from 'src/styles/modalTokens'

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

interface PendingInvitationsModalProps {
  isOpen: boolean
  invitations: MyInvitation[]
  onClose: () => void
}

/**
 * SN-7849 (C) — the popup web Home shows once per sign-in while invitations are
 * waiting. Each row names the team, the role and who sent it, and Review opens
 * that invitation. View Invitations opens the bell on its Invitations tab; Not
 * Now just closes (the card stays).
 */
const PendingInvitationsModal = ({
  isOpen,
  invitations,
  onClose,
}: PendingInvitationsModalProps) => {
  'use memo'

  const history = useHistory()
  const panel = useNotificationsPanel()
  const rows = invitations.map(toPendingInvitationRow)

  const review = (invitationId: string) => {
    onClose()
    history.push(`${ROUTES.INVITE_V2_ROOT}${invitationId}`)
  }

  const viewInvitations = () => {
    onClose()
    panel.open('invitations')
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={capitalizeWords(pendingInvitationsCopy.modalTitle(rows.length))}
      desktopWidth='576px'
      actions={
        <div css={footerStyle}>
          <Button
            variant='outlined'
            text={capitalizeWords(pendingInvitationsCopy.notNow)}
            onClick={onClose}
            style={fitContentStyle}
          />
          <Button
            variant='primary'
            text={capitalizeWords(pendingInvitationsCopy.viewInvitations)}
            onClick={viewInvitations}
            style={fitContentStyle}
          />
        </div>
      }
    >
      <div css={bodyStyle}>
        <Text variant='paragraphMedium' weight='regular' color={colors.WHITE}>
          {pendingInvitationsCopy.modalBody(rows.length)}
        </Text>
        <ul css={listStyle}>
          {rows.map((row) => (
            <li key={row.id} css={rowStyle}>
              <Image
                width={40}
                height={40}
                src={resolveAssetUrl(row.image) ?? TEAM_PLACEHOLDER}
                fallback={TEAM_PLACEHOLDER}
                preview={false}
                alt=''
                css={logoStyle}
              />
              <div css={rowTextStyle}>
                <Text
                  variant='paragraphMedium'
                  weight='semiBold'
                  color={colors.WHITE}
                >
                  {row.name}
                </Text>
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  {[
                    row.role,
                    row.sender && pendingInvitationsCopy.invitedBy(row.sender),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </div>
              <Button
                size='compact'
                variant='outlined'
                text={capitalizeWords(pendingInvitationsCopy.review)}
                onClick={() => review(row.id)}
              />
            </li>
          ))}
        </ul>
      </div>
    </BaseModal>
  )
}

export default PendingInvitationsModal

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
`

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: ${colors.SECONDARY_200};
`

const logoStyle = css`
  border-radius: 8px;
  object-fit: cover;
`

const rowTextStyle = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;

  @media (max-width: ${MODAL_TOKENS.MOBILE_BREAKPOINT}px) {
    flex-direction: column;
  }
`

const fitContentStyle = css`
  flex: 0 0 auto;
`
